import { HttpEvent } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, from, last, map, mergeMap, Observable, of, tap, toArray } from "rxjs";
import { toReportTemplateDto } from "../sync-mapper";
import { OnPremiseSyncDataState } from "./on-premise-sync-state.service";
import { IndividualReportTemplateDbService } from "../../database/individual-report-template-db.service";
import { ReportTemplateService } from "../../http-api/report-template.service";
import { IndividualReportTemplateSchema } from "../../../_offline/models/individual-report-template.schema";
import { ImagePath, ReportTemplate } from "../../../_offline/models/report-preview/report-template";
import { FileSystemAccessService } from "../../file-system-access.service";
import { UtilsService } from "../../_utils.service";

@Injectable({
  providedIn: "root"
})
export class ReportTemplateSyncService {
  constructor(
    private reportTemplateService: ReportTemplateService,
    private individualReportTemplateDbService: IndividualReportTemplateDbService,
    private syncState: OnPremiseSyncDataState,
    private fileSystemAccessService: FileSystemAccessService,
    private utilsService: UtilsService
  ) {}

  public createReportTemplates(): Observable<ReportTemplate[]> {
    const inspectionTemplates = this.syncState.syncDataState.inspectionTemplate;
    if (Object.keys(inspectionTemplates).length === 0) {
      return of([]);
    }

    return this.individualReportTemplateDbService.getAllOnPremise().pipe(
      mergeMap((schemas) =>
        from(
          schemas
            .map((schema) => ({ schema, createdModelIds: this.resolveCloudIds(schema.assetTypeIds ?? schema.subjectModelIds, inspectionTemplates) }))
            .filter(({ createdModelIds }) => createdModelIds.length > 0)
        )
      ),
      mergeMap(({ schema, createdModelIds }) => this.postReportTemplate(schema, createdModelIds)),
      toArray()
    );
  }

  private resolveCloudIds(subjectModelIds: string | null, inspectionTemplates: Record<string, string>): string[] {
    if (!subjectModelIds) return [];
    try {
      const parsed = JSON.parse(subjectModelIds);
      const ids: string[] = Array.isArray(parsed) ? parsed : typeof parsed === "string" ? [parsed] : [];
      return ids.filter((id) => id in inspectionTemplates).map((id) => inspectionTemplates[id]);
    } catch {
      return [];
    }
  }

  private postReportTemplate(reportTemplateSchema: IndividualReportTemplateSchema, createdModelIds: string[]): Observable<ReportTemplate> {
    const reportTemplateNew = toReportTemplateDto(reportTemplateSchema, createdModelIds);
    return this.reportTemplateService.addReportTemplate(reportTemplateNew).pipe(
      tap((reportTemplate) => this.syncState.updateReportTemplate(reportTemplateSchema.id, reportTemplate.id)),
      mergeMap((reportTemplate) => this.uploadTemplateImages(reportTemplateSchema, reportTemplate.id).pipe(map(() => reportTemplate)))
    );
  }

  private uploadTemplateImages(
    reportTemplateSchema: IndividualReportTemplateSchema,
    cloudTemplateId: string
  ): Observable<(HttpEvent<object> | null)[]> {
    if (!reportTemplateSchema.imagePathList) return of([]);
    const imagePathList = JSON.parse(reportTemplateSchema.imagePathList) as ImagePath[];
    if (!imagePathList.length) return of([]);
    return from(imagePathList).pipe(
      mergeMap((imagePath) =>
        from(this.fileSystemAccessService.getFile(imagePath.src, imagePath.id, this.utilsService.getMimeType(imagePath.id))).pipe(
          mergeMap((file) => this.reportTemplateService.updateReportTemplateImage(cloudTemplateId, file).pipe(last())),
          catchError(() => of(null))
        )
      ),
      toArray()
    );
  }
}
